import { observable, action, makeObservable } from 'mobx'
import * as R from 'ramda'
import uuidv4 from 'uuid/v4'
import i18n from 'i18next'

import { MultipleChoiceQuestion } from '../../../Models/Question'
import { cloneDeep } from 'lodash'

class QuestionPipeStore {
    questionList

    qlistQuestion

    questionMap

    @observable isLoading = true

    @observable showModal

    @observable targetQuestionId

    @observable targetQuestion

    @observable options = []

    @observable mapping = {}

    constructor(appStore) {
        makeObservable(this)
        this.appStore = appStore
    }

    @action
    load = async (qlistQuestion, respondentType, productType) => {
        this.showModal = true

        const questions = await this.getAITargetQuestions({
            oid: 1,
            isGlobal: 1,
            clientId: 1,
            respondentType,
            productType,
            questionType: 'CH',
            includeLagom: 1,
        })

        this.questionMap = questions.reduce((map, { questionId, question }) => (
            Object.assign(map, {
                [questionId.toUpperCase()]: new MultipleChoiceQuestion(JSON.parse(question)),
            })
        ), {})
        this.questionList = questions
            .map(({ questionId, title }) => ({
                value: questionId,
                label: title,
            }))

        this.qlistQuestion = qlistQuestion
        this.initState()
    }

    @action
    initState = () => {
        const { qlistQuestion, questionMap } = this
        const mapping = R.pathOr({}, ['mapping'], qlistQuestion)

        this.targetQuestionId = R.pathOr(undefined, ['targetQuestionId'], mapping)
        this.targetQuestion = this.questionList.find(({ value }) => value === this.targetQuestionId)
        this.mapping = Object.entries(R.pathOr({}, ['mapping'], mapping)).reduce((map, [k, v]) => (
            Object.assign(map, {
                [uuidv4()]: {
                    name: k,
                    optionId: v,
                },
            })
        ), {})

        const question = R.pathOr({}, [this.targetQuestionId], questionMap)
        const locale = 'en_GB'
        const initialLocale = R.pathOr(locale, ['initialLocale'], question)

        this.options = R.pathOr([], ['optionSet', 'options'], question)
            .map((option) => ({
                value: option.id,
                label: option.getTitle(locale, locale, initialLocale),
            }))
        this.isLoading = false
    }

    @action
    changeQuestion = (obj) => {
        const { questionMap } = this
        const { value } = obj

        const question = R.pathOr({}, [value], questionMap)
        const locale = 'en_GB'
        const initialLocale = R.pathOr(locale, ['initialLocale'], question)

        this.options = R.pathOr([], ['optionSet', 'options'], question)
            .map((option) => ({
                value: option.id,
                label: option.getTitle(locale, locale, initialLocale),
            }))
        this.targetQuestionId = value
        this.targetQuestion = obj
        this.mapping = {}
    }

    getAITargetQuestions = async ({
        surveyType = null,
        oid = null,
        clientId = null,
        includeLagom = null,
        questionType = null,
        isGlobal = true,
        respondentType,
        productType,
    }) => {
        try {
            let url = '/qlist/getquestions?'
            if (oid) url += `&oid=${oid}`
            if (clientId) url += `&clientId=${clientId}`
            if (questionType !== null) url += `&questionType=${questionType}`
            if (includeLagom !== null) url += `&includeLagom=${includeLagom}`
            url += '&cols=reactQlist'
            if (surveyType) url += `&surveyType=${surveyType}`
            url += `&respondentType=${respondentType}`
            url += `&productType=${productType}`
            url += `&isGlobal=${isGlobal}`
            const questions = await this.appStore.http.get(url)
            return questions
        } catch (e) {
            this.showModal = false
            this.appStore.addNotification(null, 'Error getting target questions', 'error')
            return []
        }
    }

    removeOptionFromMap = (id) => {
        const { mapping } = this
        const newMapping = cloneDeep(mapping)

        Reflect.deleteProperty(newMapping, id)
        this.mapping = newMapping
    }

    save = async () => {
        const {
            targetQuestionId,
            mapping,
        } = this

        const questionMapping = Object.values(mapping).reduce((map, { name, optionId }) => (
            Object.assign(map, {
                [name]: optionId,
            })
        ), {})

        await this.update({
            targetQuestionId,
            mapping: questionMapping,
        })
    }

    update = async (mapping) => {
        const {
            appStore,
            qlistQuestion,
        } = this

        try {
            await qlistQuestion.updateMapping(mapping)
            appStore.addNotification(null, i18n.t('qlist.aiPiping.update.success'), 'success')
            this.hide()
        } catch (e) {
            appStore.addNotification(null, i18n.t('qlist.aiPiping.update.error'), 'error')
        }
    }

    discard = async () => this.update(null)

    addOptiontoMap = () => {
        const { mapping } = this
        const newMapping = cloneDeep(mapping)
        newMapping[uuidv4()] = { name: '', optionId: undefined }

        this.mapping = newMapping
    }

    changeValue = (id, name, value) => {
        const { mapping } = this
        const newMapping = cloneDeep(mapping)

        newMapping[id][name] = value

        this.mapping = newMapping
    }

    hide = () => {
        this.showModal = false
    }
}

export default QuestionPipeStore
